import { useState, type ReactNode } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { SubscriptionResponse } from '@super/shared-types'
import { CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { billingClient } from '@/lib/api/billing.client'

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      color: '#111',
      fontFamily: 'inherit',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#dc2626' },
  },
  hidePostalCode: false,
}

const ERROR_COPY: Record<string, string> = {
  card_declined: 'Your card was declined. Try another card.',
  insufficient_funds: 'Insufficient funds. Try another card.',
  expired_card: 'That card has expired. Enter a different card.',
  incorrect_cvc: 'The security code was wrong. Double-check the CVC.',
  processing_error: 'Payment processing hiccup. Try again in a moment.',
  authentication_required:
    'This card needs authentication. Complete the prompt from your bank.',
  setup_intent_invalid:
    'Payment setup expired. Refresh the page and try again.',
  billing_unknown: "Couldn't save the card. Try again.",
}

function serverErrorToCopy(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code?: string }).code
    if (code && ERROR_COPY[code]) return ERROR_COPY[code]
  }
  return ERROR_COPY.billing_unknown
}

function stripeClientErrorToCopy(code: string | undefined): string {
  return code && ERROR_COPY[code]
    ? ERROR_COPY[code]
    : ERROR_COPY.billing_unknown
}

export interface StripeCardFormProps {
  submitSetupIntent: (setupIntentId: string) => Promise<SubscriptionResponse>
  onSuccess: (subscription: SubscriptionResponse) => void
  onError?: (message: string) => void
  ctaLabel?: string
  ctaHint?: string
  /** Blocks the CTA even when Stripe is otherwise ready. */
  disabled?: boolean
  /**
   * Runs at the start of submit. Return `false` to abort — the form does not
   * create a SetupIntent and no error state renders. Useful for gating on
   * upstream UI validity (e.g. T&C checkbox) that owns its own error surface.
   */
  onBeforeSubmit?: () => boolean
  /** Small helper text rendered directly beneath the card input. */
  inputHint?: ReactNode
  islandComponentId?: string
  ctaPlacement?: 'island' | 'inline'
}

/** Must be rendered inside a Stripe `<Elements>` boundary. */
export function StripeCardForm({
  submitSetupIntent,
  onSuccess,
  onError,
  ctaLabel = 'Start trial',
  ctaHint,
  disabled = false,
  onBeforeSubmit,
  inputHint,
  islandComponentId = 'onboarding-billing',
  ctaPlacement = 'island',
}: StripeCardFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!stripe || !elements || submitting) return
    if (onBeforeSubmit && !onBeforeSubmit()) return

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      const msg = "Card form isn't ready yet. Give it a moment and try again."
      setError(msg)
      onError?.(msg)
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const { clientSecret } = await billingClient.createSetupIntent()

      const { setupIntent, error: confirmError } =
        await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement },
        })
      if (confirmError || !setupIntent) {
        const msg = stripeClientErrorToCopy(confirmError?.code)
        setError(msg)
        onError?.(msg)
        return
      }

      const subscription = await submitSetupIntent(setupIntent.id)
      onSuccess(subscription)
    } catch (err) {
      console.error('Card submission failed', err)
      const msg = serverErrorToCopy(err)
      setError(msg)
      onError?.(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const cardReady = Boolean(stripe && elements)
  const canSubmit = cardReady && !submitting && !disabled

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-1">
        <div
          aria-invalid={error != null}
          className="border-border aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 rounded-md border bg-white p-4 transition-shadow aria-invalid:ring-[3px]"
        >
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={() => {
              if (error) setError(null)
            }}
          />
        </div>
        {error && <p className="text-destructive ml-3 text-xs">{error}</p>}
        {inputHint}
      </div>

      {ctaPlacement === 'island' ? (
        <FloatingActionIslandContent
          show={true}
          componentId={islandComponentId}
          priority={10}
        >
          <div className="pointer-events-auto flex items-center gap-3">
            {ctaHint && (
              <span className="text-muted-foreground text-xs whitespace-nowrap">
                {ctaHint}
              </span>
            )}
            <Button onClick={handleSubmit} disabled={!canSubmit} size="sm">
              <CreditCard className="mr-1.5 h-3.5 w-3.5" />
              {submitting ? 'Saving…' : ctaLabel}
            </Button>
          </div>
        </FloatingActionIslandContent>
      ) : (
        <div className="flex items-center justify-end gap-3">
          {ctaHint && (
            <span className="text-muted-foreground text-xs">{ctaHint}</span>
          )}
          <Button onClick={handleSubmit} disabled={!canSubmit} size="sm">
            <CreditCard className="mr-1.5 h-3.5 w-3.5" />
            {submitting ? 'Saving…' : ctaLabel}
          </Button>
        </div>
      )}
    </div>
  )
}
